import { useEffect, useMemo, useState } from "react";
import { Check, Lock, Minus, Pencil, Plus, RotateCcw, Shield, ShieldAlert, Trash2, Users } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, InlineAlert, Input, NativeSelect, Skeleton, Switch, Textarea, Tooltip, cn, notifySaved, toast } from "@ncp/ui";
import { usePermissionCatalogue, useRoleActions, useRoles, type PermissionArea, type PermissionInfo, type Role, type RoleInput } from "./api";
import { useWorkspaces } from "../workspaces/api";

const ROLE_COLORS = ["#8a6d1f", "#2e5b6f", "#5b7f3b", "#8f4f2a", "#6b4b8a", "#a63d40", "#3d6a8a", "#556b2f"];

/** The platform administrator role: superuser, never editable. */
const isSuperuser = (r: Role) => r.permissions.includes("*");

/** Whether a role's declared permission list covers a permission (exact, area wildcard `area.*`, or superuser `*`). */
function grants(perms: string[], key: string): "direct" | "wildcard" | false {
  if (perms.includes(key)) return "direct";
  if (perms.includes("*")) return "wildcard";
  const area = key.split(".")[0];
  if (perms.includes(`${area}.*`)) return "wildcard";
  return false;
}

/** Roles & permissions: the matrix (roles × permissions grouped by area) with inline toggles, plus create/edit/delete dialogs. */
export function RolesMatrix({ creating, onCreatingChange }: { creating: boolean; onCreatingChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const roles = useRoles();
  const catalogue = usePermissionCatalogue();
  const actions = useRoleActions();
  const canWrite = can("platform.roles.write");
  const [editing, setEditing] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const [areaFilter, setAreaFilter] = useState<string>("all");
  const [showDisabled, setShowDisabled] = useState(false);

  const areas = useMemo(() => (catalogue.data ?? []).filter((a) => areaFilter === "all" || a.area === areaFilter), [catalogue.data, areaFilter]);
  const visibleRoles = useMemo(() => (roles.data ?? []).filter((r) => showDisabled || r.enabled), [roles.data, showDisabled]);

  const toggle = (role: Role, perm: PermissionInfo) => {
    if (!canWrite || isSuperuser(role)) return;
    const state = grants(role.permissions, perm.key);
    if (state === "wildcard") { toast.info(t("admin:identity.roles.wildcardHint")); return; }
    const next = state === "direct" ? role.permissions.filter((p) => p !== perm.key) : [...role.permissions, perm.key];
    actions.update.mutate({ key: role.key, name: role.name, nameAr: role.nameAr, description: role.description, descriptionAr: role.descriptionAr, permissions: next, rank: role.rank, assignable: role.assignable, color: role.color, enabled: role.enabled, departmentKey: role.departmentKey });
  };

  if (roles.isLoading || catalogue.isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <InlineAlert tone="info">{t("admin:identity.roles.hint")}</InlineAlert>

      <div className="grid gap-3 md:grid-cols-3">
        {visibleRoles.map((r) => (
          <Card key={r.key} className={cn(!r.enabled && "opacity-60")}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="inline-block size-3 rounded-full" style={{ background: r.color }} aria-hidden />
                  <CardTitle className="text-base">{locale === "ar" && r.nameAr ? r.nameAr : r.name}</CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  {r.isBuiltIn ? <Tooltip content={t("admin:identity.roles.builtInHint")}><Badge variant="outline" size="sm"><Lock /> {t("admin:identity.roles.builtIn")}</Badge></Tooltip> : null}
                  {!r.enabled ? <Badge variant="outline" size="sm">{t("admin:identity.roles.disabled")}</Badge> : null}
                </div>
              </div>
              <CardDescription className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px]">{r.key}</span>
                {r.departmentKey ? <Badge variant="outline" size="sm" data-testid={`role-dept-${r.key}`}>{t("admin:identity.roles.departmentBadge", { department: r.departmentKey })}</Badge> : null}
                <span className="inline-flex items-center gap-1"><Users className="size-3.5" /> {t("admin:identity.roles.members", { count: r.members })}</span>
                <span>{t("admin:identity.roles.permissionCount", { count: r.permissions.includes("*") ? t("admin:identity.roles.all") : r.effectivePermissions.length })}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-2 pt-0">
              <p className="line-clamp-2 text-sm text-muted-foreground">{(locale === "ar" && r.descriptionAr) || r.description || "—"}</p>
              {canWrite ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)} aria-label={t("admin:common.edit")} disabled={isSuperuser(r)}><Pencil /></Button>
                  {r.isBuiltIn && !isSuperuser(r) ? <Tooltip content={t("admin:identity.roles.resetHint")}><Button size="sm" variant="ghost" loading={actions.reset.isPending && actions.reset.variables === r.key} onClick={() => actions.reset.mutate(r.key, { onSuccess: (answer) => notifySaved(answer, t("admin:identity.roles.toasts.reset")) })} aria-label={t("admin:identity.roles.reset")}><RotateCcw /></Button></Tooltip> : null}
                  {!r.isBuiltIn ? <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} aria-label={t("admin:common.delete")} disabled={r.members > 0}><Trash2 /></Button> : null}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
        {canWrite ? (
          <button type="button" onClick={() => onCreatingChange(true)} className="flex min-h-28 items-center justify-center gap-2 rounded-2xl border border-dashed border-sand-300 bg-sand-50/60 text-sm font-medium text-aegold-800 transition-colors hover:border-aegold-400 hover:bg-aegold-50/60">
            <Plus className="size-4" /> {t("admin:identity.roles.newRole")}
          </button>
        ) : null}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base"><Shield className="size-4 text-aegold-700" /> {t("admin:identity.roles.matrix")}</CardTitle>
              <CardDescription>{t("admin:identity.roles.matrixHint")}</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={showDisabled} onCheckedChange={setShowDisabled} /> {t("admin:identity.roles.showDisabled")}</label>
              <NativeSelect value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} className="h-8 w-44 text-xs">
                <option value="all">{t("admin:identity.roles.allAreas")}</option>
                {(catalogue.data ?? []).map((a) => <option key={a.area} value={a.area}>{t(`admin:identity.areas.${a.area}`, { defaultValue: a.area })}</option>)}
              </NativeSelect>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto scrollbar-thin" tabIndex={0}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-sand-50/95 text-xs uppercase tracking-wider text-sand-700 backdrop-blur">
                <tr>
                  <th className="sticky start-0 z-20 min-w-64 bg-sand-50/95 px-4 py-2 text-start font-medium">{t("admin:identity.roles.permission")}</th>
                  {visibleRoles.map((r) => (
                    <th key={r.key} className="min-w-24 px-2 py-2 text-center font-medium">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-block size-2.5 rounded-full" style={{ background: r.color }} aria-hidden />
                        <span className="max-w-24 truncate normal-case tracking-normal" title={r.name}>{locale === "ar" && r.nameAr ? r.nameAr : r.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => (
                  <AreaRows key={a.area} area={a} roles={visibleRoles} canWrite={canWrite} onToggle={toggle} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <RoleDialog open={creating || !!editing} role={editing} onOpenChange={(v) => { if (!v) { onCreatingChange(false); setEditing(null); } }} catalogue={catalogue.data ?? []} onSubmit={(input, key) => {
        const done = (r: unknown) => { notifySaved(r, t(key ? "admin:identity.roles.toasts.saved" : "admin:identity.roles.toasts.created")); onCreatingChange(false); setEditing(null); };
        if (key) actions.update.mutate({ ...input, key }, { onSuccess: done });
        else actions.create.mutate(input, { onSuccess: done });
      }} busy={actions.create.isPending || actions.update.isPending} />

      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent size="sm">
          <DialogHeader><DialogTitle>{t("admin:identity.roles.deleteTitle")}</DialogTitle><DialogDescription>{t("admin:identity.roles.deleteConfirm", { name: deleting?.name })}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>{t("admin:common.cancel")}</Button>
            <Button variant="destructive" loading={actions.remove.isPending} onClick={() => deleting && actions.remove.mutate(deleting.key, { onSuccess: (answer) => { notifySaved(answer, t("admin:identity.roles.toasts.deleted")); setDeleting(null); } })}><Trash2 /> {t("admin:common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AreaRows({ area, roles, canWrite, onToggle }: { area: PermissionArea; roles: Role[]; canWrite: boolean; onToggle: (r: Role, p: PermissionInfo) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <tr className="bg-sand-100/60">
        <td colSpan={roles.length + 1} className="sticky start-0 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-sand-700">{t(`admin:identity.areas.${area.area}`, { defaultValue: area.area })}</td>
      </tr>
      {area.permissions.map((p) => (
        <tr key={p.key} className="border-t border-sand-100 hover:bg-sand-50/60">
          <td className="sticky start-0 z-10 bg-white/95 px-4 py-2 backdrop-blur">
            <div className="flex items-center gap-2">
              <span className="font-medium">{p.name}</span>
              {p.dangerous ? <Tooltip content={t("admin:identity.roles.dangerous")}><ShieldAlert className="size-3.5 text-danger-600" /></Tooltip> : null}
            </div>
            <p className="text-xs text-muted-foreground">{p.description} <span className="font-mono text-[10px] text-sand-500">{p.key}</span></p>
          </td>
          {roles.map((r) => {
            const state = grants(r.permissions, p.key);
            const locked = isSuperuser(r) || !canWrite;
            return (
              <td key={r.key} className="px-2 py-2 text-center">
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => onToggle(r, p)}
                  aria-label={`${r.name}: ${p.name}`}
                  aria-pressed={!!state}
                  className={cn(
                    "inline-flex size-7 items-center justify-center rounded-md border transition-colors",
                    state === "direct" && "border-success-100 bg-success-50 text-success-700",
                    state === "wildcard" && "border-aegold-200 bg-aegold-50 text-aegold-800",
                    !state && "border-sand-200 text-sand-400",
                    locked ? "cursor-default" : "hover:border-aegold-400",
                  )}
                  title={state === "wildcard" ? t("admin:identity.roles.viaWildcard") : undefined}
                >
                  {state ? <Check className="size-4" /> : <Minus className="size-3.5" />}
                </button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function RoleDialog({ open, role, onOpenChange, catalogue, onSubmit, busy }: { open: boolean; role: Role | null; onOpenChange: (v: boolean) => void; catalogue: PermissionArea[]; onSubmit: (input: RoleInput, key?: string) => void; busy: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const workspaces = useWorkspaces();
  const [form, setForm] = useState<RoleInput>({ name: "", permissions: [], rank: 10, assignable: true, enabled: true, color: ROLE_COLORS[0] });
  const [key, setKey] = useState("");
  const dangerous = useMemo(() => catalogue.flatMap((a) => a.permissions).filter((p) => p.dangerous && form.permissions.includes(p.key)), [catalogue, form.permissions]);

  useEffect(() => {
    if (!open) return;
    if (role) { setForm({ name: role.name, nameAr: role.nameAr ?? "", description: role.description ?? "", descriptionAr: role.descriptionAr ?? "", permissions: role.permissions, rank: role.rank, assignable: role.assignable, color: role.color, enabled: role.enabled, departmentKey: role.departmentKey ?? null }); setKey(role.key); }
    else { setForm({ name: "", nameAr: "", description: "", descriptionAr: "", permissions: [], rank: 10, assignable: true, enabled: true, color: ROLE_COLORS[Math.floor(Math.random() * ROLE_COLORS.length)] }); setKey(""); }
  }, [open, role]);

  const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
  const toggleArea = (a: PermissionArea, on: boolean) => {
    const keys = a.permissions.map((p) => p.key);
    setForm((f) => ({ ...f, permissions: on ? [...new Set([...f.permissions, ...keys])] : f.permissions.filter((p) => !keys.includes(p)) }));
  };
  // Built-in roles keep their key and stay enabled, but their permissions are editable (Admin is never editable).
  const builtIn = role?.isBuiltIn ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{role ? t("admin:identity.roles.editRole") : t("admin:identity.roles.newRole")}</DialogTitle>
          <DialogDescription>{builtIn ? t("admin:identity.roles.builtInEditHint") : t("admin:identity.roles.dialogHint")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="space-y-3">
            <Field label={t("admin:common.name")} required><Input value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (!role) setKey(slug(e.target.value)); }} /></Field>
            <Field label={t("admin:common.nameAr")}><Input dir="rtl" value={form.nameAr ?? ""} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} /></Field>
            <Field label={t("admin:common.key")} hint={t("admin:identity.roles.keyHint")}><Input value={key} disabled={!!role} onChange={(e) => setKey(slug(e.target.value))} className="font-mono" /></Field>
            <Field label={t("admin:common.description")}><Textarea rows={2} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label={t("admin:common.descriptionAr")}><Textarea dir="rtl" rows={2} value={form.descriptionAr ?? ""} onChange={(e) => setForm({ ...form, descriptionAr: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("admin:identity.roles.rank")} hint={t("admin:identity.roles.rankHint")}><Input type="number" min={0} max={1000} value={form.rank} onChange={(e) => setForm({ ...form, rank: Number(e.target.value) })} /></Field>
              <Field label={t("admin:identity.roles.color")}>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {ROLE_COLORS.map((c) => <button key={c} type="button" aria-label={c} onClick={() => setForm({ ...form, color: c })} className={cn("size-6 rounded-full border-2", form.color === c ? "border-aeblack-900" : "border-transparent")} style={{ background: c }} />)}
                </div>
              </Field>
            </div>
            {!builtIn ? (
              <Field label={t("admin:identity.roles.department")} hint={t("admin:identity.roles.departmentHint")}>
                <NativeSelect data-testid="role-department" value={form.departmentKey ?? ""} onChange={(e) => setForm({ ...form, departmentKey: e.target.value || null })}>
                  <option value="">{t("admin:identity.roles.allDepartments")}</option>
                  {(workspaces.data ?? []).map((w) => <option key={w.key} value={w.key}>{locale === "ar" && w.nameAr ? w.nameAr : w.name}</option>)}
                </NativeSelect>
              </Field>
            ) : null}
            <label className="flex items-center justify-between gap-3 rounded-lg border border-sand-200 px-3 py-2 text-sm"><span>{t("admin:identity.roles.assignable")}<span className="block text-xs text-muted-foreground">{t("admin:identity.roles.assignableHint")}</span></span><Switch checked={form.assignable} onCheckedChange={(v) => setForm({ ...form, assignable: v })} /></label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-sand-200 px-3 py-2 text-sm"><span>{t("admin:identity.roles.enabled")}<span className="block text-xs text-muted-foreground">{t("admin:identity.roles.enabledHint")}</span></span><Switch checked={form.enabled} disabled={builtIn} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /></label>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{t("admin:identity.roles.permissions")} <Badge variant="outline" size="sm" className="ms-1 tabular">{form.permissions.includes("*") ? t("admin:identity.roles.all") : form.permissions.length}</Badge></p>
              <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, permissions: [] })}>{t("admin:identity.roles.clear")}</Button>
            </div>
            {form.permissions.includes("*") ? <InlineAlert tone="warning">{t("admin:identity.roles.superuser")}</InlineAlert> : null}
            {dangerous.length > 0 && !form.permissions.includes("*") ? <InlineAlert tone="warning">{t("admin:identity.roles.dangerousSelected", { count: dangerous.length })}</InlineAlert> : null}
            <div className="max-h-[52vh] space-y-3 overflow-y-auto rounded-lg border border-sand-200 p-3 scrollbar-thin">
              {catalogue.map((a) => {
                const keys = a.permissions.map((p) => p.key);
                const selected = keys.filter((k) => form.permissions.includes(k)).length;
                const wildcard = form.permissions.includes(`${a.area}.*`) || form.permissions.includes("*");
                return (
                  <div key={a.area}>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sand-700">
                        <Checkbox disabled={wildcard} checked={wildcard ? true : selected === keys.length ? true : selected > 0 ? "indeterminate" : false} onCheckedChange={(v) => toggleArea(a, v === true)} />
                        {t(`admin:identity.areas.${a.area}`, { defaultValue: a.area })}
                      </label>
                      <span className="text-xs tabular text-muted-foreground">{wildcard ? t("admin:identity.roles.all") : `${selected}/${keys.length}`}</span>
                    </div>
                    <div className="grid gap-1 sm:grid-cols-2">
                      {a.permissions.map((p) => {
                        const on = wildcard || form.permissions.includes(p.key);
                        return (
                          <label key={p.key} className={cn("flex items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors", on ? "bg-aegold-50/60" : "hover:bg-sand-50")}>
                            <Checkbox className="mt-0.5" disabled={wildcard} checked={on} onCheckedChange={(v) => setForm((f) => ({ ...f, permissions: v === true ? [...f.permissions, p.key] : f.permissions.filter((k) => k !== p.key) }))} />
                            <span className="min-w-0">
                              <span className="flex items-center gap-1 font-medium">{p.name}{p.dangerous ? <ShieldAlert className="size-3 text-danger-600" /> : null}</span>
                              <span className="block text-xs text-muted-foreground">{p.description}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" loading={busy} disabled={!form.name.trim() || (!role && !key)} onClick={() => onSubmit({ ...form, key: role ? undefined : key }, role?.key)}>{t("admin:common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
